import {
  ComponentVariant,
  ComponentShadow,
  ComponentAppearance,
} from 'src/modules/shared/types/component.type';

/**
 * Soft appearance styles - light background with colored tints
 */
export const panelSoftClasses: Record<
  ComponentVariant,
  {
    base: string;
    hover: string;
    active: string;
  }
> = {
  primary: {
    base: 'bg-primary-50',
    hover: 'hover:bg-primary-100',
    active: 'bg-primary-100',
  },
  secondary: {
    base: 'bg-secondary-50',
    hover: 'hover:bg-secondary-100',
    active: 'bg-secondary-100',
  },
  neutral: {
    base: 'bg-white',
    hover: 'hover:bg-neutral-100',
    active: 'bg-neutral-100',
  },
  danger: {
    base: 'bg-danger-50',
    hover: 'hover:bg-danger-100',
    active: 'bg-danger-100',
  },
  warning: {
    base: 'bg-warning-50',
    hover: 'hover:bg-warning-100',
    active: 'bg-warning-100',
  },
  success: {
    base: 'bg-success-50',
    hover: 'hover:bg-success-100',
    active: 'bg-success-100',
  },
  info: {
    base: 'bg-info-50',
    hover: 'hover:bg-info-100',
    active: 'bg-info-100',
  },
  whatsapp: {
    base: 'bg-whatsapp-50',
    hover: 'hover:bg-whatsapp-100',
    active: 'bg-whatsapp-100',
  },
  call: {
    base: 'bg-call-50',
    hover: 'hover:bg-call-100',
    active: 'bg-call-100',
  },
  email: {
    base: 'bg-email-50',
    hover: 'hover:bg-email-100',
    active: 'bg-email-100',
  },
};

/**
 * Filled appearance styles - solid colored backgrounds
 */
export const panelFilledClasses: Record<
  ComponentVariant,
  {
    base: string;
    hover: string;
    active: string;
  }
> = {
  primary: {
    base: 'bg-primary-400',
    hover: 'hover:bg-primary-500',
    active: 'bg-primary-500',
  },
  secondary: {
    base: 'bg-secondary-400',
    hover: 'hover:bg-secondary-500',
    active: 'bg-secondary-500',
  },
  neutral: {
    base: 'bg-neutral-400',
    hover: 'hover:bg-neutral-500',
    active: 'bg-neutral-500',
  },
  danger: {
    base: 'bg-danger-400',
    hover: 'hover:bg-danger-500',
    active: 'bg-danger-500',
  },
  warning: {
    base: 'bg-warning-400',
    hover: 'hover:bg-warning-500',
    active: 'bg-warning-500',
  },
  success: {
    base: 'bg-success-400',
    hover: 'hover:bg-success-500',
    active: 'bg-success-500',
  },
  info: {
    base: 'bg-info-400',
    hover: 'hover:bg-info-500',
    active: 'bg-info-500',
  },
  whatsapp: {
    base: 'bg-whatsapp-400',
    hover: 'hover:bg-whatsapp-500',
    active: 'bg-whatsapp-500',
  },
  call: {
    base: 'bg-call-400',
    hover: 'hover:bg-call-500',
    active: 'bg-call-500',
  },
  email: {
    base: 'bg-email-400',
    hover: 'hover:bg-email-500',
    active: 'bg-email-500',
  },
};

/**
 * Outline appearance styles - bordered with white background
 */
export const panelOutlineClasses: Record<
  ComponentVariant,
  {
    base: string;
    hover: string;
    active: string;
  }
> = {
  primary: {
    base: 'border border-primary-300 bg-white',
    hover: 'hover:bg-primary-50',
    active: 'bg-primary-50',
  },
  secondary: {
    base: 'border border-secondary-300 bg-white',
    hover: 'hover:bg-secondary-50',
    active: 'bg-secondary-50',
  },
  neutral: {
    base: 'border border-neutral-300 bg-white',
    hover: 'hover:bg-neutral-50',
    active: 'bg-neutral-50',
  },
  danger: {
    base: 'border border-danger-300 bg-white',
    hover: 'hover:bg-danger-50',
    active: 'bg-danger-50',
  },
  warning: {
    base: 'border border-warning-300 bg-white',
    hover: 'hover:bg-warning-50',
    active: 'bg-warning-50',
  },
  success: {
    base: 'border border-success-300 bg-white',
    hover: 'hover:bg-success-50',
    active: 'bg-success-50',
  },
  info: {
    base: 'border border-info-300 bg-white',
    hover: 'hover:bg-info-50',
    active: 'bg-info-50',
  },
  whatsapp: {
    base: 'border border-whatsapp-300 bg-white',
    hover: 'hover:bg-whatsapp-50',
    active: 'bg-whatsapp-50',
  },
  call: {
    base: 'border border-call-300 bg-white',
    hover: 'hover:bg-call-50',
    active: 'bg-call-50',
  },
  email: {
    base: 'border border-email-300 bg-white',
    hover: 'hover:bg-email-50',
    active: 'bg-email-50',
  },
};

/**
 * Ghost appearance styles - minimal with only text color
 */
export const panelGhostClasses: Record<
  ComponentVariant,
  {
    base: string;
    hover: string;
    active: string;
  }
> = {
  primary: {
    base: 'bg-transparent',
    hover: 'hover:bg-primary-50',
    active: 'bg-primary-100',
  },
  secondary: {
    base: 'bg-transparent',
    hover: 'hover:bg-secondary-50',
    active: 'bg-secondary-100',
  },
  neutral: {
    base: 'bg-transparent',
    hover: 'hover:bg-neutral-50',
    active: 'bg-neutral-100',
  },
  danger: {
    base: 'bg-transparent',
    hover: 'hover:bg-danger-50',
    active: 'bg-danger-100',
  },
  warning: {
    base: 'bg-transparent',
    hover: 'hover:bg-warning-50',
    active: 'bg-warning-100',
  },
  success: {
    base: 'bg-transparent',
    hover: 'hover:bg-success-50',
    active: 'bg-success-100',
  },
  info: {
    base: 'bg-transparent',
    hover: 'hover:bg-info-50',
    active: 'bg-info-100',
  },
  whatsapp: {
    base: 'bg-transparent',
    hover: 'hover:bg-whatsapp-50',
    active: 'bg-whatsapp-100',
  },
  call: {
    base: 'bg-transparent',
    hover: 'hover:bg-call-50',
    active: 'bg-call-100',
  },
  email: {
    base: 'bg-transparent',
    hover: 'hover:bg-email-50',
    active: 'bg-email-100',
  },
};

/**
 * Map of appearance types to their style classes
 */
export const panelAppearanceClasses: Record<
  NonNullable<ComponentAppearance>,
  Record<
    ComponentVariant,
    {
      base: string;
      hover: string;
      active: string;
    }
  >
> = {
  soft: panelSoftClasses,
  filled: panelFilledClasses,
  outline: panelOutlineClasses,
  ghost: panelGhostClasses,
};

export const panelBaseClasses = 'rounded-lg transition-all duration-200';

export const panelNeumorphicClasses: Record<ComponentShadow, string> = {
  inset: 'shadow-inner',
  'inset-sm': 'shadow-sm',
  flat: 'shadow-md bg-white',
  none: 'shadow-none',
  sm: 'shadow-sm',
  md: 'shadow-md',
  lg: 'shadow-lg',
  xl: 'shadow-xl',
  '2xl': 'shadow-2xl',
  inner: 'shadow-inner',
};
